import {
  Box,
  Chip,
  CircularProgress,
  Typography,
} from "@mui/material";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import { useEffect, useRef, useState } from "react";
import { axiosInstance } from "../../../utils/networking";
import dayjs from "dayjs";
import { BROADCAST_TYPES } from "./broadcastTypes";

const StatusChip = ({ status }: { status: string }) => {
  const colorMap: Record<string, "default" | "info" | "success" | "warning" | "error"> = {
    queued: "default",
    sending: "info",
    sent: "success",
    error: "error",
  };
  return (
    <Chip
      label={status}
      size="small"
      color={colorMap[status] || "default"}
      sx={{ textTransform: "capitalize" }}
    />
  );
};

const columns: GridColDef[] = [
  {
    field: "createdAt",
    headerName: "Date",
    width: 160,
    renderCell: (params) =>
      params.value ? dayjs(params.value).format("MMM D, YYYY h:mm A") : "—",
  },
  {
    field: "broadcastType",
    headerName: "Type",
    width: 160,
    renderCell: (params) =>
      BROADCAST_TYPES.find((t) => t.id === params.value)?.label || params.value,
  },
  { field: "subject", headerName: "Subject", flex: 1, minWidth: 200 },
  { field: "fromBFC", headerName: "Sent By", width: 160 },
  {
    field: "recipientCount",
    headerName: "Recipients",
    width: 110,
    align: "right",
    headerAlign: "right",
    renderCell: (params) =>
      params.value != null ? params.value.toLocaleString() : "—",
  },
  {
    field: "status",
    headerName: "Status",
    width: 110,
    renderCell: (params) => <StatusChip status={params.value} />,
  },
];

const BroadcastHistory = () => {
  const [rows, setRows] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const fetchBroadcasts = async () => {
    try {
      const res = await axiosInstance.get("/broadcast");
      const broadcasts = (res.data.broadcasts || []).map((b: any) => ({
        ...b,
        id: b._id,
      }));
      setRows(broadcasts);
      return broadcasts;
    } catch (err) {
      console.error("Failed to fetch broadcasts:", err);
      return [];
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBroadcasts().then((broadcasts) => {
      const hasSending = broadcasts.some(
        (b: any) => b.status === "queued" || b.status === "sending",
      );
      if (hasSending) {
        pollRef.current = setInterval(async () => {
          const updated = await fetchBroadcasts();
          const stillSending = updated.some(
            (b: any) => b.status === "queued" || b.status === "sending",
          );
          if (!stillSending && pollRef.current) {
            clearInterval(pollRef.current);
            pollRef.current = null;
          }
        }, 5000);
      }
    });

    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, []);

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (rows.length === 0) {
    return (
      <Box sx={{ py: 6, textAlign: "center" }}>
        <Typography color="text.secondary">
          No broadcasts sent yet. Create your first broadcast in the "New Broadcast" tab.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ height: 500 }}>
      <DataGrid
        rows={rows}
        columns={columns}
        pageSizeOptions={[10, 25, 50]}
        initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
        disableRowSelectionOnClick
        sx={{
          "& .MuiDataGrid-columnHeaders": { backgroundColor: "#f5f5f5" },
          "& .MuiDataGrid-row:hover": { backgroundColor: "#fce4ec" },
        }}
      />
    </Box>
  );
};

export default BroadcastHistory;
